import React, { useState, useEffect } from "react";
import axios from "axios";
import "./App.css";
import Footer from "./Components/Footer";
import CartSection from "./Components/CartSection";
import MenuSection from "./Components/MenuSection";
import SearchSection from "./Components/SearchSection";
import RestInfo from "./Components/RestInfo";
import { SnackbarProvider, enqueueSnackbar } from "notistack";
import { CartProvider, useCart } from "./CartContext";
import { SearchProvider } from "./SearchContext";
import Orders from "./Components/Orders";
import Home from "./pages/Home";
import QrCart from "./Components/Cart/QrCart";
import { isQrEntry } from "./helper/isQrEntry";
import { resetSession } from "./helper/resetSession";
import { variantLineId, resolvePackageCharge } from "./helper/variants";
import Login from "./Components/login";
import Product from "./pages/Product";
import Menu from "./pages/Menu";
import Cart from "./pages/Cart";
import { Routes, Route, useLocation, useNavigate } from "react-router-dom";
import "bootstrap-icons/font/bootstrap-icons.css";
import PopupInfo from "./Components/PopupInfo";
import { _get, _post } from "./services/apiClient";
import { RestaurantDetailsProvider } from "./context/RestaurantDetailsContext";
import { ShopInfo } from "./shopinfo";
import { getAppConfig } from "./utilis/appConfig";

function App() {
  const config = getAppConfig();
  const [cart, setCart] = useState(
    sessionStorage.getItem("cartValue") == null
      ? []
      : JSON.parse(sessionStorage.getItem("cartValue")),
  );
  const [phoneNumber, setPhoneNumber] = useState("");
  const [pageVisible, setPageVisible] = useState(false);
  const [ordersVisible, SetOrdersVisible] = useState(false);
  const [loggedIn, setLoggedIn] = useState(false);
  const [showPopup, setShowPopup] = useState(true);
  const [showPlacedOrderSummary, setShowPlacedOrderSummary] = useState(false);
  const [joinToken, setJoinToken] = useState(null);

  const [form, setForm] = useState({
    name: "",
    phoneNumber: "",
    email: "",
    address: "",
    tableNo: "",
  });

  //newly added
  const [restaurantStatus, setRestaurantStatus] = useState("online");

  // Initialize restaurantId in localStorage
  useEffect(() => {
    const initializeRestaurantId = async () => {
      if (localStorage.getItem("restaurantId")) {
        // console.log("✅ RestaurantId already exists in storage");
        return;
      }

      try {
        // console.log("🔄 Fetching restaurantId from database...");
        const res = await axios.get(
          `${process.env.REACT_APP_BASE_URL}/restaurant/default`
        );

        if (res.data?.success && res.data.restaurantId) {
          localStorage.setItem("restaurantId", res.data.restaurantId);
          // console.log("✅ RestaurantId initialized:", res.data.restaurantId);
        } else {
          console.error("❌ No default restaurant configured in DB");
        }
      } catch (err) {
        console.error("❌ Error fetching restaurantId:", err);
      }
    };

    initializeRestaurantId();
  }, []);


  useEffect(() => {
    const baseURL = process.env.REACT_APP_BASE_URL;
    axios
      .get(`${baseURL}/settings/restaurantstatus`)
      .then((res) => {
        setRestaurantStatus(res.data.status);
        // console.log("Fetched restaurant status:", res.data.status);
      })
      .catch((err) => console.error("Failed to fetch restaurant status", err));
  }, []);

  const location = useLocation();
  const navigate = useNavigate();
  const hideLayout = location.pathname === "/login";

  // Offline pauses only online/takeaway orders - customers seated at a table
  // (QR / dine-in session) can still order, so they never see it as offline.
  const menuStatus = localStorage.getItem("tableSession")
    ? "online"
    : restaurantStatus;

  // Scroll to top on every route change
  useEffect(() => {
    window.scrollTo(0, 0);
  }, [location.pathname]);

  useEffect(() => {
    const params = new URLSearchParams(location.search);
    const shareToken = params.get("share");

    if (!shareToken) return;

    (async () => {
      try {
        const res = await _post("/allocation/join-by-link", {
          token: shareToken,
        });

        const { sessionToken, ...sessionInfo } = res.data;
        localStorage.setItem("allocationId", sessionInfo.allocationId);
        localStorage.setItem("tableSession", sessionToken);
        localStorage.setItem("guestInfo", JSON.stringify(sessionInfo));

        setShowPopup(false);
        setPageVisible(true);

        // Navigate to home page after joining via share link
        navigate("/");
      } catch {
        enqueueSnackbar("Invalid or expired link", { variant: "error" });
      }
    })();
  }, [location, navigate, enqueueSnackbar]);

  useEffect(() => {
    const queryParams = new URLSearchParams(location.search);
    const tableNo = queryParams.get("table");

    if (tableNo) {
      setForm((prev) => ({
        ...prev,
        tableNo,
      }));

      if (!localStorage.getItem("allocationId")) {
        setShowPopup(true);
      } else {
        setShowPopup(false);
      }

      setPageVisible(true);
    } else {
      setShowPopup(false);
      setPageVisible(false);
    }
  }, [location]);

  useEffect(() => {
    const interval = setInterval(
      async () => {
        const allocationId = localStorage.getItem("allocationId");
        // console.log("Polling allocation status for ID:", allocationId);
        if (!allocationId) return;

        const endTableSession = () => {
          resetSession(); // allocation, table session, guest info, cart
          setCart([]);
          setJoinToken(null); // Clear join token to show login icon
          setPageVisible(false);
          navigate("/");
        };

        // Session from before table session tokens: scan the QR again
        if (!localStorage.getItem("tableSession")) {
          endTableSession();
          return;
        }

        try {
          const res = await _get(`/allocation/status/${allocationId}`);
          // console.log("API response status:", res.data.status);
          const status = res.data.status;
          // if (res.data.joinToken) {
          //   setJoinToken(res.data.joinToken);
          // }

          if (status === "completed" || status === "cancelled") {
            endTableSession();
          }
        } catch (err) {
          // Expired or invalid table session
          if (err.response?.status === 401 || err.response?.status === 403) {
            endTableSession();
            return;
          }
          console.error("Error polling allocation status:", err);
        }
      },
      1 * 10 * 1000,
    );

    return () => clearInterval(interval);
  }, [navigate]);

  // selectedVariant is set for multi-variant products: each chosen variant
  // (+ add-ons) becomes its own cart line, keyed by variantLineId.
  const handleClick = (menuItem, selectedAddOns = [], selectedVariant = null) => {
    const qrFlow = isQrEntry();
    const lineId = selectedVariant
      ? variantLineId(menuItem._id, selectedVariant, selectedAddOns)
      : menuItem._id;

    const isDuplicate = cart.some((item) => {
      if (selectedVariant) {
        return item._id === lineId && !item.readonly;
      }
      if (!qrFlow) {
        return item.productId === menuItem._id || item._id === menuItem._id;
      }

      return item.productId === menuItem._id && !item.readonly;
    });

    if (isDuplicate) {
      enqueueSnackbar("Item already added", { variant: "error" });
      return;
    }

    const variant = selectedVariant || menuItem.variants?.[0] || null;
    const newItem = {
      ...menuItem,
      _id: lineId,
      productId: menuItem._id,
      readonly: false,
      addOns: selectedAddOns,
      packageCharge: resolvePackageCharge(menuItem, variant),
      ...(selectedVariant && {
        variantId: selectedVariant._id,
        variantUnit: selectedVariant.unit,
        // Existing price lookups read item.variants[0].
        variants: [selectedVariant],
      }),
    };

    setCart((prev) => {
      const updated = [...prev, newItem];
      sessionStorage.setItem("cartValue", JSON.stringify(updated));
      return updated;
    });
  };

  return (
    <SnackbarProvider>
      <RestaurantDetailsProvider>
        <CartProvider>
          <SearchProvider>
            <div style={{ backgroundColor: "white", minHeight: "100vh" }}>
              <div style={{ minHeight: "100vh" }}>
                <>
                  {!hideLayout && (
                    <CartSection
                      phoneNumber={phoneNumber}
                      setPhoneNumber={setPhoneNumber}
                      pageVisible={pageVisible}
                      setPageVisible={setPageVisible}
                      form={form}
                      setForm={setForm}
                      loggedIn={loggedIn}
                      setLoggedIn={setLoggedIn}
                      SetOrdersVisible={SetOrdersVisible}
                      setShowPlacedOrderSummary={setShowPlacedOrderSummary}
                    />
                  )}

                  {ordersVisible && (
                    <Orders
                      SetOrdersVisible={SetOrdersVisible}
                      setPageVisible={setPageVisible}
                    />
                  )}
                  {!ordersVisible && (
                    <>
                      <Routes>
                        <Route
                          path="/"
                          element={
                            <>
                              {showPopup && (
                                <PopupInfo setShowPopup={setShowPopup} />
                              )}

                              <Home
                                handleClick={handleClick}
                                cart={cart}
                                setCart={setCart}
                                pageVisible={pageVisible}
                                restaurantStatus={menuStatus}
                              />
                            </>
                          }
                        />

                        <Route
                          path="/products/:id"
                          element={
                            <Product
                              handleClick={handleClick}
                              cart={cart}
                              setCart={setCart}
                              restaurantStatus={menuStatus}
                            />
                          }
                        />

                        <Route
                          path="/menu"
                          element={
                            <Menu
                              handleClick={handleClick}
                              cart={cart}
                              setCart={setCart}
                              restaurantStatus={menuStatus}
                            />
                          }
                        />
                        {config.clientLogin && (
                          <Route path="/login" element={<Login />} />
                        )}
                        <Route path="/orders" element={<Orders />} />
                        <Route
                          path="/cart"
                          element={
                            <Cart
                              cart={cart}
                              setCart={setCart}
                              form={form}
                              setForm={setForm}
                              phoneNumber={phoneNumber}
                              loggedIn={loggedIn}
                              restaurantStatus={restaurantStatus}
                            />
                          }
                        />
                        <Route
                          path="/qr-cart"
                          element={
                            <QrCart
                              cart={cart}
                              setCart={setCart}
                              joinToken={joinToken}
                              setJoinToken={setJoinToken}
                            />
                          }
                        />
                      </Routes>
                      {!hideLayout && <Footer />}
                    </>
                  )}
                </>
              </div>
            </div>
          </SearchProvider>
        </CartProvider>
      </RestaurantDetailsProvider>
    </SnackbarProvider>
  );
}

export default App;
